import MicIcon from "@material-ui/icons/Mic";
import RadioIcon from "@material-ui/icons/Radio";
import LibraryMusicIcon from "@material-ui/icons/LibraryMusic";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import AssignmentIcon from "@material-ui/icons/Assignment";
import ArtTrackIcon from "@material-ui/icons/ArtTrack";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import PeopleIcon from "@material-ui/icons/People";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import StorefrontIcon from "@material-ui/icons/Storefront";
import MessagesIcon from "@material-ui/icons/Message";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import SpeakerNotesIcon from "@material-ui/icons/SpeakerNotes";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import EventIcon from "@material-ui/icons/Event";
import { v4 as uuidv4 } from "uuid";
import { IImage } from "./Image";
import { IBaseEntity } from "../laco-common";
import { asyncForEach } from "../laco-common/base/utils";
import { IPage } from "../laco-common/base/models/page";
import { IAudioFile } from "./audio";
import { capitalize, isEmptyHtmlContent } from "../utils";
import { WidgetType, WIDGET_TYPES_ARR, WidgetConfig } from "../laco-common/base/models/widget";
import { SelectOption } from "../laco-common/base/form-validation/dynamic";
import { ImageWithLayersBG } from "../components/ui/Media/ImageWithLayersBG";
import { QuotesCollection } from "./quote";

// export type ICMSContentCollection = { [key: string]: ICMSContent };

// export type ICMSContent = {
// 	page: RootPage | string;
// };

// page templates or widgets
// i like widgets
// but potentially there could be more site templates to load the widgets into.
// in this design the single template is fine

// ISection
// configurable section types - text, form
// form would be an id

// honor u can only be a text page

// if there's an icon the h1 isn't highlighted

// dynamic, editable content
// media - can be audio, video, image - see Interview with Anna Firewives
// quote - if there's more than 1 displays as a gallery
// gallery - events to gallery page
// form widget - must have at least formid

export type ISiteWidget = {
	id: string;
	title?: string;
	widgetType: WidgetType;
	params?: any;
	widgetProps?: any;
	component?;
	componentProps?: any;
};

export type ISiteTextContent = IBaseEntity & {
	content: string;
	pageId: string;
	order: number;
	displayInHeader?: boolean;
	bgColor?: ContentBackgroundColor;
	noPadding?: boolean;
};

export type ISiteQuotesContent = IBaseEntity & QuotesCollection;

export type ContentBackgroundColor = "white" | "black" | "gray";

export type ISiteCustomWidget = IBaseEntity & {
	title?: string;
	bgColor?: ContentBackgroundColor;
	nestedWidgets?: WidgetType[];
	content?: string;
	textBlocks?: ISiteContentBlock[];
};

export type ButtonLinkType = "default" | "link" | "primary" | "info" | "secondary" | "warning" | "danger" | "success";

export type ISiteContentBlock = {
	title?: string;
	isMainHeading?: boolean;
	content?: string;
	iconUrl?: string;
	media?: ISiteMedia; // from /site/media folder
	renderSimpleMedia?: boolean;
	displayImageWLayersBg?: boolean;
	linkText?: string;
	linkUrl?: string;
	isInternalLink?: boolean;
	buttonLinkType?: ButtonLinkType;
	buttonMarginLeft?: number;
	xlButton?: boolean;
	preserveOrderOnMobile?: boolean;
	textFirst?: boolean;
	fullWidth?: boolean;
	textGrow?: boolean;
	imageGrow?: boolean;
	alignTop?: boolean;
	widgets?: WidgetType[];
};

export type ISiteMedia = Partial<IImage> & {
	filename: string;
	noFullscreen?: boolean;
	mediaDate?: number | "string" | any; // string as date format
	renderSimple?: boolean;
};

export type ISiteGalleryContent = IBaseEntity & {
	title: string;
	headline?: string;
	media: IImage[];
	folder?: string; // storage path
};

export type ISiteAudioContent = IBaseEntity & {
	title: string;
	subHeading?: string;
	files: IAudioFile[];
};

export type ILink = {
	title: string;
	path: string;
	isExternal?: boolean;
	hide?: boolean;
};

// TODO: create widget preview page - still needs to check permissions
// TODO: create user roles when editing - user that can only edit text, user that can edit widgets

export const getWidgets = (widgetsConfig: WidgetType[]): ISiteWidget[] => {
	if (!widgetsConfig || !widgetsConfig.length) return null;
	const widgets: ISiteWidget[] = [];
	widgetsConfig.forEach((w: WidgetType) => {
		widgets.push({ widgetType: w, id: w });
	});
	return widgets;
};

export const getPageWidgets = async (page: IPage): Promise<ISiteWidget[]> => {
	const widgetConfig: WidgetConfig[] = page.widgets;
	if ((!widgetConfig || !widgetConfig.length) && !page.content) return null;
	const widgets: ISiteWidget[] = [];
	let contentWidget: ISiteWidget = null;
	if (page.id === "home") {
		widgets.push({
			id: undefined,
			widgetType: null,
			component: ImageWithLayersBG,
			componentProps: { url: page.headerInnerImage, fullSize: true },
		});
	}
	if (!isEmptyHtmlContent(page.content)) {
		const formPageFound = (widgetConfig || []).find((w: WidgetType) => w === "form-content-page");
		if (!formPageFound) {
			contentWidget = { id: undefined, widgetType: "cms-content" };
			if (page.id !== "streams") {
				widgets.push(contentWidget);
			}
		}
	}
	if (!widgetConfig || !widgetConfig.length) return widgets;

	await asyncForEach(widgetConfig, async (w: WidgetConfig) => {
		if (typeof w === "string") {
			widgets.push({ widgetType: w, id: w });
		} else {
			// document reference - not in use because of serialization in redux store
			const doc: firebase.firestore.DocumentSnapshot = await w.get();
			const widget: ISiteWidget = doc.data() as ISiteWidget;
			widgets.push({ id: doc.id, ...widget });
		}
	});

	if (page.id == "streams" && contentWidget) {
		widgets.push(contentWidget);
	}

	return widgets;
};

export const getMeaningfulNewId = (data, getTooltipDisplay?: (data) => string): string => {
	let name: string;
	if (getTooltipDisplay) name = getTooltipDisplay(data);
	if (!name) name = data?.name || data?.title || data?.display || data?.label;
	if (name) {
		name = removeSpecials(name).toLowerCase().replace(/\s/g, "-");
		console.log("getMeaningfulNewId() generated:", name);
		return name;
	}

	function removeSpecials(str) {
		const lower = str.toLowerCase();
		const upper = str.toUpperCase();
		let res = "";
		for (let i = 0; i < lower.length; ++i) {
			if (!isNaN(lower[i])) res += str[i];
			if (lower[i] != upper[i] || lower[i] === "-") res += str[i];
		}
		return res;
	}

	return undefined;
};

export const DUMMY_ID_PREFIX: string = "cms-dummy-id-";

export const generateDummyId = (): string => {
	const uuid = uuidv4();
	return `${DUMMY_ID_PREFIX}${uuid}`;
};

export const getFriendlyDisplayName = (val: string): string => {
	return capitalize(val.replace(/-/g, " "));
};

export const getWidgetIcon = (w: WidgetType, page) => {
	switch (w) {
		case "audio":
			return LibraryMusicIcon; // RadioIcon
		case "gallery":
			return PhotoLibraryIcon;
		// form-submissions link to form submissions
		case "forms-list": // link to forms management
			return AssignmentIcon;
		case "form":
		case "form-content-page":
		case "form-content-page2":
		case "form-submissions":
			return AssignmentIcon;
		case "officers":
			return null;
		// return PeopleOutlineIcon;
		case "directors":
			return SupervisorAccountIcon;
		case "editor-content": {
			return null; // TODO
			// return TextFieldsIcon;
		}
		case "marketplace":
		case "marketplace-preview":
			return StorefrontIcon; // link to Marketplace
		case "messages":
		case "messages-preview":
			return MessagesIcon; // link to Marketplace
		case "events-calendar":
		case "upcoming-events": {
			return EventIcon; // link to Events
		}
		case "streams":
		case "streams-preview":
			return MenuBookIcon;
		case "checkout":
		case "store":
			return ShoppingCartIcon; // link to Ecwid management
		case "honoru":
		case "honoru-featured": {
			return null;
		}
		case "quotes":
			return SpeakerNotesIcon;
		default: {
			if (w.startsWith("custom-widget-")) return ArtTrackIcon; //TextFieldsIcon; // ArtTrackIcon
		}
	}
};

// "checkout",
// 	"streams",
// 	"streams-preview",
// 	"upcoming-events",
// 	"events-calendar",
// 	"forms-list",
// 	"form-content-page",
// 	"form", // requires formId
// 	"honoru",
// 	"honoru-featured",
// 	"officers",
// 	"directors",
// 	"gallery",
// 	"audio",
// 	"quotes",
// 	"editor-content",
// 	"cms-content",
// 	// "custom-widget-", // e.g custom-widget-associationBenefits
// 	"store",
// 	"peer-support",
// 	"form-submissions",
// 	"ps-home",
// 	"ps-facilities",
// 	"ps-agents",
// 	"ps-admin-monthly-reports",
// 	"ps-form-submissions",
// 	"marketplace",
// 	"marketplace-preview",
// 	"cms-editor",
// 	"cms-edit-collection",
// 	"cms-forms",
// 	"cms-users",
// 	"cms-pages",

export const getCustomWidgetId = (widgetType: string): string => {
	if (!widgetType.startsWith("custom-widget-")) return undefined;
	return widgetType.substr(14);
};

export const getSiteWidgetTypes = () => {
	return WIDGET_TYPES_ARR.filter((w: WidgetType) => !w.startsWith("cms-"));
};

export type IEditableWidget = {
	editWidget?: any;
	onInput?: (e: React.FormEvent<HTMLElement>) => any;
};

export const getWidgetTypeOptions = (customWidgets: ISiteCustomWidget[], siteOnly?: boolean): SelectOption[] => {
	const widgetTypes: SelectOption[] = !siteOnly
		? WIDGET_TYPES_ARR
				// .filter(
				// 		(w: WidgetType) => w.startsWith("cms-") || w.startsWith("ps-") || w === "form-submissions"
				//   )
				.map((w: WidgetType) => {
					return { label: w, value: w };
				})
		: getSiteWidgetTypes().map((w: WidgetType) => {
				return { label: w, value: w };
		  });
	(customWidgets || []).map((c: ISiteCustomWidget) => {
		const name: string = `custom-widget-${c.id}`;
		widgetTypes.push({ label: name, value: name });
	});

	return widgetTypes;
};
